import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import React, { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import CancelButton from 'components/buttons/CancelButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { HHFormTextField } from 'components/form-fields/v5'
import T from 'T'
import { handleError } from 'utils/error'
import { isEnterOrNumpadEnterWithCtrlOrMeta } from 'utils/keyboard'
import { get } from 'utils/lodash'

import PciNoteWarning from './PciNoteWarning'

type NoteFormValues = { note: string }

type ExistingNote = { id?: string; note?: string }

// Minimal structural type for the RTK Query note-mutation hooks passed in by the
// note accordions. RTK 1.8.3 does not export TypedUseMutation, so we hand-roll it.
type UseNoteMutation = () => readonly [
  (arg: { id: string; noteId?: string; data: NoteFormValues }) => { unwrap: () => Promise<unknown> },
  { isLoading: boolean }
]

type AddUpdateNoteDialogProps = {
  id: string
  isOpen?: boolean
  title?: string
  existingNote?: ExistingNote
  showPciWarning?: boolean
  useCreateNoteMutation: UseNoteMutation
  useUpdateNoteMutation: UseNoteMutation
  onClose: () => void
}

const AddUpdateNoteDialog = ({
  id,
  isOpen = false,
  title = T.NEW_NOTE,
  existingNote = {},
  showPciWarning = false,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  onClose,
}: AddUpdateNoteDialogProps) => {
  const [createNote, { isLoading: isCreateNoteLoading }] = useCreateNoteMutation()
  const [updateNote, { isLoading: isUpdateNoteLoading }] = useUpdateNoteMutation()
  const addUpdateNoteForm = useForm<NoteFormValues>({ defaultValues: { note: '' } })
  const {
    control,
    reset,
    setFocus,
    handleSubmit,
    formState: { isDirty, errors },
  } = addUpdateNoteForm

  const noteHelperText = get(errors, 'note.message')

  const handleSave = useCallback(
    (data: NoteFormValues) => {
      if (!isDirty) {
        onClose()
        return
      }

      const noteId = get(existingNote, 'id')
      if (noteId) {
        updateNote({ id, noteId, data })
          .unwrap()
          .then(() => {
            toast.success('Note updated successfully')
            onClose()
          })
          .catch(handleError)
        return
      }

      createNote({ id, data })
        .unwrap()
        .then(() => {
          toast.success('Note added successfully')
          onClose()
        })
        .catch(handleError)
    },
    [createNote, existingNote, id, isDirty, onClose, updateNote]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isEnterOrNumpadEnterWithCtrlOrMeta(event)) {
        handleSubmit(handleSave)()
      }
    },
    [handleSubmit, handleSave]
  )

  useEffect(() => {
    if (isOpen) {
      reset({ note: get(existingNote, 'note', '') })
      setTimeout(() => setFocus('note'), 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <HHBaseDialog fullWidth maxWidth="sm" open={isOpen} onClose={onClose}>
      <HHDialogTitle title={title} onClose={onClose} />
      <DialogContent onKeyDown={handleKeyDown}>
        <FormProvider {...addUpdateNoteForm}>
          <HHFormTextField
            fullWidth
            multiline
            control={control}
            deprecatedLabel={false}
            helperText={noteHelperText}
            label="Note"
            minRows={4}
            name="note"
            placeholder="Leave a note"
            rules={{ required: T.CANNOT_BE_EMPTY }}
            sx={{ mt: 3, '& .MuiOutlinedInput-root': { height: 'inherit' } }}
          />
        </FormProvider>
        {showPciWarning && <PciNoteWarning />}
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <Button
          color="primary"
          loading={isCreateNoteLoading || isUpdateNoteLoading}
          size="small"
          variant="contained"
          onClick={handleSubmit(handleSave)}
        >
          {T.SAVE}
        </Button>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

export default AddUpdateNoteDialog
